import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Blueprint from '../components/Blueprint';

const { FiShield, FiCpu, FiTruck, FiArrowRight, FiLock, FiInfo, FiLayers } = FiIcons;

const ProductAlchemist = () => {
  const mainFeatures = [
    { 
      icon: FiShield, 
      title: "Reinraum-Plattform", 
      desc: "HEPA-H14-Filtration, Überdruck-Atmosphäre und ISO-7-nahe Bedingungen. Die anwendungsneutrale Basis für kontaminationsfreie Kultivierung." 
    },
    { 
      icon: FiCpu, 
      title: "CoreVoice KI", 
      desc: "NVIDIA-Jetson-basierte Echtzeit-Überwachung. Lückenloser Audit-Trail als Grundlage für Qualifizierungen (DQ/IQ/OQ/PQ) und GMP-Dokumentation." 
    },
    { 
      icon: FiTruck, 
      title: "Schlüsselfertige Mobilität", 
      desc: "Am Hub vollständig montiert und getestet, per Sattelzug geliefert und vor Ort mit Mobilkran in unter 24 Stunden voll einsatzbereit." 
    }
  ];

  return (
    <div className="pt-24 bg-white">
      {/* Hero Section */}
      <section className="py-20 bg-stone-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-stone-200 border border-stone-300 mb-6">
                <span className="text-[10px] font-bold text-stone-600 uppercase tracking-widest">Pharma-Grade Plattform</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-stone-900 mb-6 leading-tight">
                The Alchemist <span className="text-emerald-600">1.0</span>
              </h1>
              <p className="text-lg text-stone-600 mb-8 leading-relaxed">
                Eine autarke Hochleistungsplattform für die kontrollierte Kultivierung biologischer Kulturen auf pharmazeutischem Niveau. Container, Klimahülle, KI-Steuerung und Sicherheitstechnik bilden die anwendungsneutrale Basis — die Ausstattung wird je nach Anwendung konfiguriert.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="#contact" className="bg-stone-900 text-white px-8 py-4 rounded-xl font-bold hover:bg-black transition-all flex items-center gap-2 shadow-xl shadow-stone-200">
                  Spezifikation anfragen <SafeIcon icon={FiArrowRight} />
                </a>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="relative">
              <img 
                src="https://media-manager-c.questera.ai/greta-media/6a4e1632643f5ecd6b4b884d4518abad28ee28269a896d1a9bb8c081180db7d41fc79629d873bafacdf5fb95106d974d/images/aW1hZ2UvcG5n/4c83981534ca2e0e770e4cfaaff2de6c.png" 
                className="rounded-[40px] shadow-2xl border border-stone-200 relative z-10" 
                alt="The Alchemist 1.0 Unit" 
              />
              <div className="absolute -bottom-10 -right-10 w-64 h-64 bg-emerald-100 rounded-full blur-3xl -z-0 opacity-50" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Platform Explanation Section */}
      <section className="py-20 bg-white border-b border-stone-100">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mb-8 mx-auto">
            <SafeIcon icon={FiLayers} className="text-3xl" />
          </div>
          <h2 className="text-3xl font-bold text-stone-900 mb-6">Plattform mit konfigurierbarer Ausstattung</h2>
          <p className="text-lg text-stone-600 leading-relaxed">
            Der Alchemist besteht aus einer anwendungsneutralen Basis (Reinraum-Container, Klimahülle, CoreVoice-KI-Steuerung, Sicherheitstechnik) und einer austauschbaren Ausstattungsvariante für die eigentliche Kultivierung. Standardausstattung ist die Hydroponik-/Pflanzenkultivierung, wie im folgenden Systemschnitt gezeigt.
          </p>
        </div>
      </section>

      {/* Feature Blocks */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12">
            {mainFeatures.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group"
              >
                <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mb-8 group-hover:scale-110 group-hover:bg-emerald-600 group-hover:text-white transition-all duration-300">
                  <SafeIcon icon={f.icon} className="text-3xl" />
                </div>
                <h3 className="text-2xl font-bold text-stone-900 mb-4">{f.title}</h3>
                <p className="text-stone-500 leading-relaxed italic">
                  {f.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Blueprint />

      {/* Disclaimer Section */}
      <section className="py-16 bg-stone-50 border-y border-stone-200">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white p-8 rounded-3xl border border-emerald-100 flex gap-6 items-start">
            <div className="shrink-0 w-12 h-12 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center">
              <SafeIcon icon={FiInfo} className="text-xl" />
            </div>
            <div>
              <h4 className="font-bold text-stone-900 mb-2 text-lg">Wichtiger Hinweis zum Betrieb</h4>
              <p className="text-stone-600 text-sm leading-relaxed">
                CoreUnit Systems agiert ausschließlich als <strong>Maschinen- und Anlagenbauer</strong>. Wir liefern die technische Infrastruktur (ausgelegt nach EU-GMP Grade C) und die Steuerungssoftware. Der Betrieb der Anlage, die Einhaltung lokaler Anbaurichtlinien sowie die Erzielung spezifischer Wirkstoffprofile liegen in der alleinigen Verantwortung des Betreibers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-stone-900 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-full h-full opacity-10 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-emerald-500 blur-[160px] rounded-full" />
        </div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <div className="w-20 h-20 bg-white/10 rounded-3xl flex items-center justify-center mx-auto mb-8 backdrop-blur-md border border-white/10">
            <SafeIcon icon={FiLock} className="text-4xl text-emerald-400" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Technische Spezifikation</h2>
          <p className="text-stone-400 text-xl mb-12 max-w-2xl mx-auto leading-relaxed">
            Detaillierte CAD-Pläne, Qualifizierungs-Unterlagen und vollständige Komponentenlisten stellen wir nach Erstkontakt und NDA-Prüfung bereit.
          </p>
          <div className="bg-white/5 border border-white/10 p-8 md:p-12 rounded-[40px] backdrop-blur-xl text-left">
            <form className="grid md:grid-cols-2 gap-6" onSubmit={(e) => e.preventDefault()}>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-stone-400 ml-1">Name / Unternehmen</label>
                <input type="text" className="w-full bg-stone-800 border border-stone-700 rounded-xl px-4 py-4 text-white focus:border-emerald-500 outline-none transition-colors" placeholder="Ihre Firma GmbH" />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-stone-400 ml-1">E-Mail Adresse</label>
                <input type="email" className="w-full bg-stone-800 border border-stone-700 rounded-xl px-4 py-4 text-white focus:border-emerald-500 outline-none transition-colors" placeholder="name@firma.de" />
              </div>
              <div className="md:col-span-2 space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-stone-400 ml-1">Nachricht / Projekt-Kontext</label>
                <textarea rows="4" className="w-full bg-stone-800 border border-stone-700 rounded-xl px-4 py-4 text-white focus:border-emerald-500 outline-none transition-colors" placeholder="Kurze Beschreibung Ihres Vorhabens..."></textarea>
              </div>
              <div className="md:col-span-2">
                <button className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-5 rounded-xl transition-all shadow-lg shadow-emerald-600/20 flex items-center justify-center gap-3">
                  Unterlagen anfordern <SafeIcon icon={FiArrowRight} />
                </button>
                <p className="text-[10px] text-stone-500 mt-4 text-center">
                  Mit dem Absenden erklären Sie sich bereit, im nächsten Schritt eine elektronische Geheimhaltungsvereinbarung (NDA) zu unterzeichnen.
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductAlchemist;